import axios from 'constants/axios';
import { all, call, fork, put, takeLatest } from 'redux-saga/effects';
import { statusIsOk } from 'utils/api';

import {
  API_FETCH_WHITE_LABELLING,
  API_UPDATE_WHITE_LABELLING,
} from 'constants/appConstants';
import { addNotification } from 'containers/Notifications/actions';

import type { AxiosRequestConfig } from 'axios';
import type { BackendErrorDetails } from 'interfaces';

import { currentAccountId } from 'utils/currentAccountId';
import {
  fetchWhiteLabellingFailure,
  fetchWhiteLabellingSuccess,
  updateWhiteLabellingFailure,
  updateWhiteLabellingSuccess,
} from './actions';
import { FETCH_WHITE_LABELLING, UPDATE_WHITE_LABELLING } from './constants';

import type { IWhiteLabellingConfig } from './types';

interface WhiteLabellingAPIResponse {
  status: string;
  error: BackendErrorDetails | string;
  result: IWhiteLabellingConfig & { accountId: string };
}

const makeRequest = (config: AxiosRequestConfig) => axios(config);

const getURL = (whiteLabellingSource: string, value: string) =>
  `${API_FETCH_WHITE_LABELLING()}?${whiteLabellingSource}=${value}`;

export function* fetchWhiteLabelling({
  whiteLabellingSource,
  value,
}: {
  whiteLabellingSource: string;
  value: string;
}) {
  const url = getURL(whiteLabellingSource, value);

  const config: AxiosRequestConfig = {
    method: 'get',
    url,
  };

  try {
    const response: {
      status: number;
      data: WhiteLabellingAPIResponse;
    } = yield call(makeRequest, config);

    let isWhiteLabellingConfigured = false;

    if (response.status < 300) {
      currentAccountId.set(response.data?.result?.accountId);
    }

    if (response.status === 204) {
      yield all([
        put(fetchWhiteLabellingSuccess({}, isWhiteLabellingConfigured)),
      ]);
    } else if (statusIsOk(response)) {
      isWhiteLabellingConfigured = true;
      yield all([
        put(
          fetchWhiteLabellingSuccess(
            response.data.result,
            isWhiteLabellingConfigured
          )
        ),
      ]);
    } else {
      yield all([put(fetchWhiteLabellingFailure(response.data.error))]);
    }
  } catch (error) {
    const errorText = 'Could not fetch white labelling configuration';
    yield all([
      put(fetchWhiteLabellingFailure(errorText)),
      put(addNotification('error', errorText)),
    ]);
  }
}

export function* updateWhiteLabelling({
  accountID,
  whiteLabellingConfig,
}: {
  accountID: number;
  whiteLabellingConfig: IWhiteLabellingConfig;
}) {
  const config: AxiosRequestConfig = {
    method: 'put',
    url: API_UPDATE_WHITE_LABELLING(accountID),
    data: whiteLabellingConfig,
  };

  try {
    const response: {
      status: number;
      data: WhiteLabellingAPIResponse;
    } = yield call(makeRequest, config);

    if (statusIsOk(response)) {
      yield all([put(updateWhiteLabellingSuccess(response.data.result))]);
    } else {
      yield all([put(updateWhiteLabellingFailure(response.data.error))]);
    }
  } catch (error) {
    const errorText = 'Could not update white labelling configuration';
    yield all([
      put(updateWhiteLabellingFailure(errorText)),
      put(addNotification('error', errorText)),
    ]);
  }
}

export function* fetchWhiteLabellingWatcher() {
  // @ts-ignore
  yield takeLatest(FETCH_WHITE_LABELLING, fetchWhiteLabelling);
}

export function* updateWhiteLabellingWatcher() {
  // @ts-ignore
  yield takeLatest(UPDATE_WHITE_LABELLING, updateWhiteLabelling);
}

export function* rootSaga() {
  // @ts-ignore
  yield all([
    fork(fetchWhiteLabellingWatcher),
    fork(updateWhiteLabellingWatcher),
  ]);
}

export default [rootSaga];
